import React from 'react';
import { TableModel, ITableColumn, AggregrationType, TableRow } from '../table/table.model';
import { formatNumTwoDig } from 'app/utils';
import { Button } from '../common/Button';
import { Icon } from '../common/Icon';

export interface UwTask {
	agtId: string;
	name: string;
	amount: number;
	completed?: boolean;
}

interface CopyCellProps {
	value: string;
	copyValue?: string;
	label: string;
}

const CopyCell: React.FC<CopyCellProps> = ({ value, copyValue, label }) => {
	const [flashed, setFlashed] = React.useState(false);
	const timerRef = React.useRef<NodeJS.Timeout>();

	const handleCopy = async (e: React.MouseEvent) => {
		e.stopPropagation();
		const textToCopy = copyValue ?? value;

		try {
			await navigator.clipboard.writeText(textToCopy);
		} catch {
			// Fallback for browsers that don't support clipboard API
			const textarea = document.createElement('textarea');
			textarea.value = textToCopy;
			textarea.style.position = 'fixed';
			textarea.style.opacity = '0';
			document.body.appendChild(textarea);
			textarea.select();
			try {
				document.execCommand('copy');
			} catch {}
			document.body.removeChild(textarea);
		}

		setFlashed(true);

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		timerRef.current = setTimeout(() => {
			setFlashed(false);
		}, 1100);
	};

	return (
		<span
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: '0.5rem',
				cursor: 'pointer',
				padding: '0.25rem 0.5rem',
				margin: '-0.25rem -0.5rem',
				borderRadius: '3px',
				background: flashed ? '#d6f4dd' : 'transparent',
				transition: 'background 0.2s'
			}}
			onClick={handleCopy}
			title={`${label} kopieren`}
		>
			<span>{value}</span>
			<span
				style={{
					fontSize: '0.875rem',
					color: flashed ? '#1e8927' : '#007ab3',
					opacity: flashed ? 1 : 0.6
				}}
			>
				{flashed ? <Icon iconClass="check" faModifier="fas" /> : <Icon iconClass="copy" faModifier="far" />}
			</span>
		</span>
	);
};

export class UwTaskListTableModel extends TableModel<UwTask, string> {
	onToggleComplete?: (agtId: string) => void;

	constructor() {
		super();
		this.idProperty = 'agtId';
		this.idType = 'string';
		this.sortBy = 'agtId';
		this.sortAsc = true;

		const cols: ITableColumn<UwTask, string>[] = [];

		cols.push({
			label: 'AgtId',
			path: 'agtId',
			sortBy: 'agtId',
			width: 150,
			render: (row: TableRow<UwTask>) => (
				<CopyCell value={row.data.agtId} label="AgtId" />
			),
		});

		cols.push({
			label: 'Name',
			path: 'name',
			sortBy: 'name',
			render: (row: TableRow<UwTask>) => (
				<span style={{ textDecoration: row.data.completed ? 'line-through' : 'none' }}>
					{row.data.name}
				</span>
			),
		});

		cols.push({
			label: 'Betrag',
			path: 'amount',
			sortBy: 'amount',
			cellModifier: 'has-text-right',
			labelModifier: 'has-text-right',
			render: (row: TableRow<UwTask>) => {
				const formatted = formatNumTwoDig(row.data.amount) + ' €';
				return (
					<CopyCell
						value={formatted}
						copyValue={row.data.amount.toFixed(2).replace('.', ',')}
						label="Betrag"
					/>
				);
			},
			agg: {
				aggType: AggregrationType.sum,
				format: (val) => formatNumTwoDig(val) + ' €',
			},
		});

		cols.push({
			label: 'Status',
			path: 'completed',
			cellModifier: 'has-text-centered',
			labelModifier: 'has-text-centered',
			render: (row: TableRow<UwTask>) => {
				const done = row.data.completed;
				return done ? (
					<Button
						type="button"
						className="button is-success is-small"
						onClick={(e) => {
							e.stopPropagation();
							this.onToggleComplete?.(row.data.agtId);
						}}
						title="Als offen markieren"
					>
						<Icon iconClass="check" faModifier="fas" />
						<span>Erledigt</span>
					</Button>
				) : (
					<Button
						type="button"
						className="button is-outlined is-success is-small"
						onClick={(e) => {
							e.stopPropagation();
							this.onToggleComplete?.(row.data.agtId);
						}}
					>
						Erledigt
					</Button>
				);
			},
		});

		this.setCols(cols);
	}
}
